import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, Clock, User, Zap, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Single-source-of-truth modal for viewing the full detail of one automation run.
 * Used from both the sparkline click on /admin/automations and the row click on
 * the full-log page /admin/automations/runs.
 */
export default function AutomationRunDetailsModal({ runId, open, onOpenChange }) {
  const { token } = useAuth();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !runId) { setRun(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/automations/runs/${runId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setRun(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load run');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, runId, token]);

  const isSuccess = run?.status === 'success';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="run-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A0A0A]">
            <Zap className="w-5 h-5 text-[#002FA7]" /> Automation Run Detail
          </DialogTitle>
        </DialogHeader>
        {loading && (
          <div className="py-12 text-center text-[#9CA3AF]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        )}
        {!loading && run && (
          <div className="space-y-4" data-testid="run-details-content">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-[#0A0A0A]">{run.automation_name}</h3>
              <Badge className={`text-[10px] ${isSuccess ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'}`} data-testid="run-status-badge">
                {isSuccess ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                {run.status}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <KV icon={<Clock className="w-3 h-3" />} label="Started" value={run.created_at ? format(new Date(run.created_at), 'MMM d, yyyy HH:mm:ss') : '—'} />
              <KV icon={<Clock className="w-3 h-3" />} label="Duration" value={`${run.duration_ms || 0} ms`} />
              <KV icon={<User className="w-3 h-3" />} label="Triggered by" value={run.triggered_by || '—'} />
              <KV icon={<Zap className="w-3 h-3" />} label="Automation id" value={run.automation_id} mono />
            </div>

            <div>
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Run id</p>
              <p className="text-[11px] font-mono text-[#4B5563] break-all">{run.run_id}</p>
            </div>

            {run.error && (
              <div data-testid="run-error-block">
                <p className="text-[10px] uppercase tracking-wider text-[#FF3B30] mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Error
                </p>
                <pre className="text-[11px] bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded p-3 text-[#7F1D1D] whitespace-pre-wrap break-words">
                  {run.error}
                </pre>
              </div>
            )}

            <div>
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Outcome payload</p>
              {run.outcome && Object.keys(run.outcome).length > 0 ? (
                <pre className="text-[11px] bg-[#F9FAFB] border border-[#E5E7EB] rounded p-3 text-[#0A0A0A] whitespace-pre-wrap break-words max-h-64 overflow-y-auto" data-testid="run-outcome-payload">
                  {JSON.stringify(run.outcome, null, 2)}
                </pre>
              ) : (
                <p className="text-[11px] text-[#9CA3AF] italic">No outcome payload recorded.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ icon, label, value, mono }) {
  return (
    <div>
      <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider flex items-center gap-1">{icon} {label}</p>
      <p className={`text-[#0A0A0A] mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}
